class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = '';

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        document.activeElement.getAttribute('name'),
        event.target.dataset.quantityVariantId
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  updateQuantity(line, quantity, name, variantId) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        const quantityElement =
          document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll('.cart-item');

        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        this.getSectionsToRender().forEach((section) => {
          const elementToReplace =
            document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector
          );
        });
        const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
        let message = '';
        if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
          if (typeof updatedValue === 'undefined') {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
          }
        }
        this.updateLiveRegions(line, message);

        const lineItem =
          document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper
            ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
        }

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
      })
      .catch(() => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').textContent = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}

/**dynamic cart**/
document.addEventListener('DOMContentLoaded', function () {
    const cartContainer = document.querySelector('.cart__summary');

    if (!cartContainer) return; // Ensure the cart container exists.

    // Delegate event listener for quantity input changes
    document.body.addEventListener('change', function (event) {
        if (event.target.classList.contains('quantity__input')) {
            const variantId = event.target.dataset.quantityVariantId; // Variant ID
            const quantity = parseInt(event.target.value, 10); // New quantity

            if (quantity >= 0) {
                updateCart(variantId, quantity); // Trigger AJAX cart update
            } else {
                showError('Quantity cannot be negative.'); // Optional error handling for invalid inputs
            }
        }
    });

    /**
     * Update the cart item quantity via AJAX.
     * @param {number} variantId - The variant ID to update.
     * @param {number} quantity - The new quantity.
     */
    function updateCart(variantId, quantity) {
        fetch('/cart/change.js', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                id: variantId,
                quantity: quantity,
            }),
        })
            .then(response => {
                if (!response.ok) {
                    throw new Error('Network response was not ok.');
                }
                return response.json();
            })
            .then(cart => {
                console.log(cart); // For debugging purposes
                updateCartSummary(cart); // Update cart summary
                rebindQuantityInputs(); // Ensure bindings are refreshed
            })
            .catch(error => {
                console.error('Error updating cart:', error);
                showError('An error occurred while updating the cart.');
            });
    }

    /**
     * Update the cart summary prices.
     * @param {object} cart - The updated cart object from Shopify.
     */
    // function updateCartSummary(cart) {
      
    //     // Update the total price
    //     const totalPriceElement = document.querySelector('.cart__summary-price .totals__total-value');
    //     totalPriceElement.innerText = formatMoney(cart.total_price); // Use custom formatter

    //     // Update the delivery charges (if applicable)
    //     const deliveryChargesElement = document.querySelector('.cart__summary-delivery .totals__total-value');
    //     const shippingMethod = cart.shipping_rate ? cart.shipping_rate.price : 0; // Replace with dynamic shipping logic if needed
    //     deliveryChargesElement.innerText = shippingMethod > 0 ? formatMoney(shippingMethod) : 'Free';

    //     // Update the grand total
    //     const grandTotalElement = document.querySelector('.cart__summary-total .totals__total-value');
    //     const grandTotal = cart.total_price + (shippingMethod || 0);
    //     grandTotalElement.innerText = formatMoney(grandTotal);

    //     // Update discount message (if applicable)
    //     const discountMessageElement = document.querySelector('.cart__summary-discount .cart__discount-message');
    //     if (cart.total_discount && cart.total_discount > 0) {
    //       var discountElement = document.querySelector('.discount-percentage .discount');
    //       var offerElement = document.querySelector('.cart__summary-discount');
    //       if (discountElement) {
    //           // Style the element
    //           offerElement.style.borderTop = '2px solid #e1e1e1';
      
    //           // Retrieve the discount percentage text
    //           var discountText = discountElement.textContent.trim(); // Get the percentage value, e.g., "50% off"
              
    //           var discountPercentage = parseFloat(discountText.replace('% off', '').trim());          
              
    //           if (discountPercentage > 0) {
    //                      offerElement.style.borderTop = '2px solid #e1e1e1';
    //           }
      
    //           // Display the message with the discount percentage
    //           discountMessageElement.innerText = `You saved ${formatMoney(cart.total_discount)} (${discountText}) on this order!`;
    //       }
    //   } else {
    //       discountMessageElement.innerText = ''; // Clear the message if no discount
    //   }

    // }

function updateCartSummary(cart) {
    // Update the total price
    var item_quantity = cart.item_count;
    console.log(cart.item_count);
    const totalPriceElement = document.querySelector('.cart__summary-price .totals__total-value');
    totalPriceElement.innerText = formatMoney(cart.total_price); // Use custom formatter

    // Update the delivery charges (if applicable)
    const deliveryChargesElement = document.querySelector('.cart__summary-delivery .totals__total-value');
    const shippingMethod = cart.shipping_rate ? cart.shipping_rate.price : 0; // Replace with dynamic shipping logic if needed
    deliveryChargesElement.innerText = shippingMethod > 0 ? formatMoney(shippingMethod) : 'Free';

    // Update the grand total
    const grandTotalElement = document.querySelector('.cart__summary-total .totals__total-value');
    const grandTotal = cart.total_price + (shippingMethod || 0);
    grandTotalElement.innerText = formatMoney(grandTotal);

    // Update discount message (if applicable)
    const discountMessageElement = document.querySelector('.cart__summary-discount .cart__discount-message');
    
      if (cart.total_discount && cart.total_discount !== 0) {
          var offerElement = document.querySelector('.cart__summary-discount');
          
          // Style the element to indicate there's a discount
          offerElement.style.borderTop = '2px solid #e1e1e1';
      
          // Calculate the discount percentage
          var originalTotalPrice = cart.items.reduce(function(acc, item) {
              return acc + item.original_line_price;
          }, 0); // Sum up the original line prices of all items in the cart
      
          var discountPercentage = (cart.total_discount / originalTotalPrice) * 100;
          discountPercentage = Math.round(discountPercentage); // Round to nearest integer
      
          // Display the message with the discount percentage
          discountMessageElement.innerText = `You will save ${discountPercentage}% on this order!`;
      } else {
          // Clear the message if no discount
          discountMessageElement.innerText = `You will save 0% on this order!`;
      }


    // Update item count dynamically
    const itemCountElement = document.querySelector('.totals__total span');
    itemCountElement.innerText = item_quantity;
}



    
    /**
     * Rebind Quantity Input Events.
     * Ensures quantity inputs work correctly after DOM updates.
     */
    function rebindQuantityInputs() {
        const inputs = document.querySelectorAll('.quantity__input');
        inputs.forEach(input => {
            input.addEventListener('change', function () {
                const variantId = this.dataset.quantityVariantId;
                const quantity = parseInt(this.value, 10);

                if (quantity >= 0) {
                    updateCart(variantId, quantity);
                }
            });
        });
    }

    /**
     * Display an error message.
     * @param {string} message - The error message to display.
     */
       function showError(message) {
          const errorElement = document.getElementById('custom-cart-errors');
          if (errorElement) {
              errorElement.innerText = message; // Set the error message text
              errorElement.style.display = 'block'; // Make the element visible
          }
        }
    

    /**
     * Format money to display in the desired currency format.
     * @param {number} amount - The amount in cents.
     * @param {string} currencySymbol - The currency symbol (e.g., "$").
     * @param {string} delimiter - The thousands delimiter (e.g., ",").
     * @param {string} decimalSeparator - The decimal separator (e.g., ".").
     * @returns {string} - The formatted money string.
     */
    function formatMoney(amount, currencySymbol = "Rs", delimiter = ",", decimalSeparator = ".") {
        const cents = amount % 100;
        const dollars = Math.floor(amount / 100);

        const formattedCents = cents < 10 ? `0${cents}` : cents;
        const formattedDollars = dollars
            .toString()
            .replace(/\B(?=(\d{3})+(?!\d))/g, delimiter); // Add thousands delimiter

        return `${currencySymbol}${formattedDollars}${decimalSeparator}${formattedCents}`;
    }
});


/**
 * Clear the error message from the #cart-errors element.
 */
function clearError() {
    const errorElement = document.getElementById('custom-cart-errors');
    if (errorElement) {
        errorElement.innerText = '';
        errorElement.style.display = 'none'; // Hide the element
    }
}